import axios from 'axios';
import type {
    ApiResponse,
    BulkCreateDynamicLinkRequest,
    BulkCreateDynamicLinkResponse,
    DynamicLinkDTO,
    DynamicUnlockResponse,
    DynamicTokenResponse,
} from '../types';

const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8080/api', // Imposta la URL del tuo backend
    headers: {
        'Content-Type': 'application/json',
    },
});

api.interceptors.request.use(config => {
    // Non sovrascrivere un Authorization già impostato dalla singola richiesta
    // (es. token breve dei link dinamici per un utente ADMIN/STAFF loggato).
    if (!config.headers.Authorization) {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
    }
    return config;
}, error => {
    return Promise.reject(error);
});

api.interceptors.response.use(response => {
    let renewedToken = response.headers['x-renewed-token'];

    if (renewedToken) {
        localStorage.setItem('token', renewedToken);
    }else {

        renewedToken = response.headers['X-Renewed-Token'];

        if (renewedToken) {
            localStorage.setItem('token', renewedToken);
        }
    }

    return response;
}, error => {
    if (error.response && error.response.status === 401) {
        localStorage.removeItem('token');
        if(!window.location.href.includes('login') &&
            !window.location.href.includes('/d/') &&
            !window.location.href.includes('/invite/') &&
            !window.location.href.includes('/notConfirmed/') &&
            !window.location.href.includes('/notApproved') &&
            !window.location.href.includes('/confirm/') &&
            !window.location.href.includes('/unauthorized') &&
            !window.location.href.includes('/privacy-policy') &&
            !window.location.href.includes('/recover-password') &&
            !window.location.href.includes('/recover') &&
            !window.location.href.includes('/reset-password') &&
            !window.location.href.includes('/terms-and-conditions')
        ) {
            window.location.href = '/login';
        }
    }
    return Promise.reject(error);
});

// --- Dynamic Links (QR rotante) -----------------------------------------
// NOTA: baseURL = REACT_APP_API_URL (es. http://localhost:8082) SENZA /api,
// quindi i path includono il prefisso /api come tutte le altre chiamate del codebase.
// NOTA: il backend avvolge ogni risposta JSON in ApiResponse<T> = { status, message, data }.
// Come nel resto del codebase, i servizi ritornano la AxiosResponse grezza e i chiamanti
// leggono il payload reale in `response.data.data`.

/** POST /api/dynamic-links/bulk — crea un link dinamico per ogni QR selezionato. */
export const createDynamicLinksBulk = (
    req: BulkCreateDynamicLinkRequest,
) => api.post<ApiResponse<BulkCreateDynamicLinkResponse>>('/api/dynamic-links/bulk', req);

/** GET /api/dynamic-links?clientId=&qrCodeId= — elenco link dinamici (senza password/secret). */
export const listDynamicLinks = (
    clientId?: number | null,
    qrCodeId?: number | null,
) => {
    const params: { clientId?: number; qrCodeId?: number } = {};
    if (clientId != null && clientId !== -1) params.clientId = clientId;
    if (qrCodeId != null) params.qrCodeId = qrCodeId;
    return api.get<ApiResponse<DynamicLinkDTO[]>>('/api/dynamic-links', { params });
};

/** PATCH /api/dynamic-links/{id}/revoke — revoca un link dinamico. */
export const revokeDynamicLink = (
    id: number,
) => api.patch<void>(`/api/dynamic-links/${id}/revoke`);

/** GET /api/dynamic-links/export — scarica l'elenco (csv|xlsx) come blob. */
export const exportDynamicLinks = (
    clientId: number | null | undefined,
    format: 'csv' | 'xlsx',
) => api.get('/api/dynamic-links/export', {
    params: {
        ...(clientId != null && clientId !== -1 ? { clientId } : {}),
        format,
    },
    responseType: 'blob',
});

/** POST /api/dynamic-links/{publicId}/unlock — endpoint PUBBLICO. Ritorna JWT breve. */
export const unlockDynamicLink = (
    publicId: string,
    password?: string,
) => api.post<ApiResponse<DynamicUnlockResponse>>(
    `/api/dynamic-links/${publicId}/unlock`,
    { password: password ?? undefined },
);

/** GET /api/dynamic-links/{publicId}/token — PUBBLICO, richiede l'accessToken breve. */
export const fetchDynamicToken = (
    publicId: string,
    accessToken: string,
) => api.get<ApiResponse<DynamicTokenResponse>>(
    `/api/dynamic-links/${publicId}/token`,
    { headers: { Authorization: `Bearer ${accessToken}` } },
);

export default api;
